import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskText, Stagger, StaggerItem } from "./Reveal";
import { scrollToId } from "@/hooks/useLenis";
import { useReserve } from "./Reserve";

const STEPS = ["Know your stage.", "Choose the destination.", "Decide what matters.", "Take the next step."];

export const Closing = () => {
  const ref = useRef(null);
  const { open } = useReserve();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section id="closing" ref={ref} className="relative bg-ocean text-cream overflow-hidden">
      <motion.img
        style={{ y }}
        src="/images/brian-boots-desk.png"
        alt=""
        aria-hidden
        className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-15 img-mono pointer-events-none"
      />
      <div className="grain absolute inset-0 pointer-events-none" />

      <div className="relative mx-auto max-w-[1200px] px-5 sm:px-8 py-28 sm:py-40">
        <Reveal>
          <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">The Invitation</span>
        </Reveal>

        <h2 className="mt-8 font-serif tracking-[-0.02em] leading-[0.96] text-4xl sm:text-5xl lg:text-[4.6rem] max-w-5xl">
          <MaskText lines={["You don't need to know", "everything that happens next.", <span key="n" className="italic text-cognac">You need a roadmap for what matters now.</span>]} />
        </h2>

        <Stagger className="mt-14 grid sm:grid-cols-4 gap-6 max-w-4xl" amount={0.1}>
          {STEPS.map((s, i) => (
            <StaggerItem key={s}>
              <div className="border-t border-white/20 pt-4">
                <span className="font-sans text-[10px] tracking-[0.2em] text-cognac">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 font-serif text-xl sm:text-2xl leading-snug">{s}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.15}>
          <p className="mt-14 font-serif italic text-2xl sm:text-3xl text-cream/85 max-w-2xl leading-snug">
            And when the business changes? <span className="text-cognac">Come back and build the next roadmap.</span>
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap items-center gap-6">
            <button
              data-testid="closing-cta"
              onClick={() => open("closing")}
              className="group font-sans text-sm uppercase tracking-[0.18em] bg-cognac text-cream px-10 py-5 hover:bg-cream hover:text-ocean transition-colors duration-400"
            >
              Create My Business Roadmap
              <span className="inline-block ml-3 transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <p data-testid="closing-launch-note" className="font-sans text-xs text-cream/55 leading-relaxed">
              <span className="uppercase tracking-[0.2em] text-cream/80">January 5, 2027</span><br />
              Founding 50 includes the private live kickoff with Brian Smith.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer className="bg-cream border-t border-[var(--line)]">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div>
          <div className="font-serif text-2xl text-ocean tracking-tight">Create Your Business Roadmap in 7 Days</div>
          <div className="mt-1 font-sans text-xs uppercase tracking-[0.24em] text-cognac">
            A planning system from Brian Smith, founder of UGG
          </div>
        </div>
        <nav className="flex flex-wrap gap-x-8 gap-y-3 font-sans text-sm text-ocean/70">
          {[["The Philosophy", "philosophy"], ["Find Your Stage", "assessment"], ["The Seven Days", "days"], ["Brian", "brian"], ["Founding 50", "founding"], ["FAQ", "faq"]].map(([l, id]) => (
            <button key={id} data-testid={`footer-${id}`} onClick={() => scrollToId(id)} className="link-underline">{l}</button>
          ))}
        </nav>
      </div>
      <div className="mt-10 hairline pt-6 flex flex-col sm:flex-row justify-between gap-3 font-sans text-xs text-ink/45">
        <span>© {new Date().getFullYear()} Brian Smith. All rights reserved.</span>
        <span>UGG is a trademark of Deckers Outdoor Corporation.</span>
      </div>
    </div>
  </footer>
);
